/**
 * The decision view wired to data (Inbox detail, Now, the Lanes inspector): resolves
 * `decision:<id>` (sealed entity) or `ask:<hub item id>` (legacy question), routes thread
 * messages and answers (decisions.ts), answers legacy questions through `run.answer`, and makes
 * every evidence reference openable on the run's machine (openable-refs).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { describeBoardAnswer, type OptionMedia } from '@viko/protocol' // visual-review
import {
  RefInspector,
  RefLink,
  SandboxFrame,
  Transcript,
  ViewerContext,
  type ViewerContextValue,
} from '@viko/transcript-ui'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useBackend } from '../../lib/backend.js'
import { pickTriageMachine } from '../../lib/bark-store.js'
import {
  type AnswerInput,
  type DecisionView as DecisionModel,
  legacyAnswers,
  viewFromDecision,
  viewFromLegacy,
} from '../../lib/decision-view.js'
import { decisionRoute } from '../../lib/decisions.js'
import { runHref } from '../../lib/ia.js' // ia-shell
import { LED_TURNS, ledTurns } from '../../lib/led-turns.js' // ia-shell
import { matchesDecision } from '../../lib/now-inbox.js'
import { useProgressSync } from '../../lib/progress-snapshot.js'
import { qk, useInbox, useMachines, useProjects, useRuns, useTasks } from '../../lib/queries.js'
import { useRefStore } from '../../lib/refs.js'
import { useRunTranscript } from '../../lib/session.js'
import type { InboxItem, RunSummary } from '../../lib/types.js'
import type { PendingFile } from '../../lib/uploads.js'
import { ArtifactGallery } from '../Artifacts.js'
import { EncryptedArtifact } from '../EncryptedArtifact.js'
import { EncryptedSnapshot } from '../EncryptedSnapshot.js' // visual-review
import { useAskActions } from '../inbox/useAskActions.js'
import { snapshotRenderer } from '../SnapshotFrame.js' // visual-review
import { Empty, errorText, SkeletonRows, useToast } from '../ui.js'
import { DecisionView } from './DecisionView.js'
import { DECISIONS_KEY, useDecisions } from './useDecisions.js'

/** `decision:<id>` / `ask:<hub id>` (hub ids contain colons). */
export function parseDecisionKey(key: string): { kind: string; id: string } {
  const i = key.indexOf(':')
  return i < 0 ? { kind: key, id: '' } : { kind: key.slice(0, i), id: key.slice(i + 1) }
}

export interface DecisionPanelProps {
  inspectKey: string
  /** The hub ask behind an `ask:` key when the caller already holds it. */
  item?: InboxItem
  intent?: 'reply' | 'delegate' | 'freeform'
  showTitle?: boolean
  keys?: boolean
  /** Called after an answer or snooze (lists move to the next row). */
  onDone?: () => void
  /** Open a task (the Lanes inspector); default: the project's Lanes tab. */
  onOpenTask?: (taskId: string, projectId: string) => void
  /** ia-shell: n / v / ⇧V keys (off when several decisions are expanded, e.g. a batch). */
  hotkeys?: boolean
}

export function useDecisionModel(inspectKey: string, given?: InboxItem) {
  const decisions = useDecisions()
  const inbox = useInbox()
  const { kind, id } = parseDecisionKey(inspectKey)
  return useMemo(() => {
    const now = Date.now()
    if (kind === 'decision') {
      const d = decisions.data?.find((x) => x.id === id)
      if (!d) return { pending: decisions.isPending && !!decisions.store, view: undefined }
      const item = inbox.data?.find((i) => matchesDecision(i, d))
      return { pending: false, view: viewFromDecision(d, now, item) }
    }
    if (kind === 'ask') {
      const item = inbox.data?.find((i) => (i.itemId ?? i.id) === id || i.id === id) ?? given
      if (!item || item.type !== 'question')
        return { pending: !given && inbox.isPending, view: undefined, item }
      // A sealed decision for this ask wins over the transient view (thread, evidence).
      const d = decisions.data?.find((x) => matchesDecision(item, x))
      if (d) return { pending: false, view: viewFromDecision(d, now, item), item }
      return { pending: false, view: viewFromLegacy(item, now), item }
    }
    return { pending: false, view: undefined }
  }, [
    kind,
    id,
    given,
    decisions.data,
    decisions.isPending,
    decisions.store,
    inbox.data,
    inbox.isPending,
  ])
}

export function DecisionPanel(p: DecisionPanelProps) {
  const model = useDecisionModel(p.inspectKey, p.item)
  if (model.pending) return <SkeletonRows n={5} />
  if (!model.view)
    return (
      <Empty center title="Nothing to decide here">
        This decision was answered, withdrawn or snoozed. Answered ones stay in the conversation.
      </Empty>
    )
  return <WiredDecision {...p} view={model.view} />
}

/**
 * Answer / snooze / message for any decision view: entity decisions through the DecisionStore
 * (routed to the asking machine, else triage), legacy questions through `run.answer`.
 */
export function useDecisionActions() {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const ask = useAskActions()
  const decisions = useDecisions()
  const store = decisions.store
  const machines = useMachines()
  const projects = useProjects()
  const routeFor = (v: DecisionModel) => {
    if (!v.decision || !store) return undefined
    const project = projects.data?.find((x) => x.id === store.projectOf(v.decision!))
    return decisionRoute(
      v.decision,
      machines.data ?? [],
      pickTriageMachine(project, machines.data ?? []),
    )
  }
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: DECISIONS_KEY })
    void qc.invalidateQueries({ queryKey: qk.inbox })
  }
  return {
    store,
    routeFor,
    /** Throws on failure (callers toast); returns false when nothing was sent. */
    async answer(v: DecisionModel, a: AnswerInput): Promise<boolean> {
      if (v.source === 'entity') {
        const route = routeFor(v)
        if (!store || !route) throw new Error('No machine can take this answer right now.')
        await store.answer(v, a, route)
        if (v.item) ask.resolveLocal([v.item.id])
      } else if (v.item) {
        if (!(await ask.answer(v.item, legacyAnswers(v, a), true))) return false
      } else return false
      refresh()
      return true
    },
    async snooze(v: DecisionModel, until: number) {
      if (v.decision && store) await store.snooze(v.decision, until)
      if (v.item) await ask.snooze(v.item, until)
      refresh()
    },
    async message(v: DecisionModel, text: string, files: PendingFile[]) {
      const d = v.decision
      const route = routeFor(v)
      if (!d || !store || !route) throw new Error('No machine can take this message right now.')
      const attachments = files.length
        ? ((await b.uploadAttachments(
            files,
            d.runId ?? `decision-${d.id}`,
            route.machineId,
          )) as never[])
        : []
      const status = await store.message(d, text, route, attachments)
      refresh()
      if (status === 'failed')
        toast('The agent could not be reached. It is marked on the message.', 'error')
      return status
    },
    toast,
  }
}

function WiredDecision(p: DecisionPanelProps & { view: DecisionModel }) {
  const { view: v } = p
  const b = useBackend()
  const nav = useNavigate()
  const act = useDecisionActions()
  const runs = useRuns()
  const machines = useMachines()
  const projects = useProjects()
  const run = v.runId ? runs.data?.find((r) => r.runId === v.runId) : undefined
  const projectId = v.decision && act.store ? act.store.projectOf(v.decision) : run?.projectId
  const project = projects.data?.find((x) => x.id === projectId)
  const tasks = useTasks(projectId ? { projectId } : {})
  const task = v.taskId ? tasks.data?.find((t) => t.id === v.taskId) : undefined
  const machine = machines.data?.find((m) => m.id === (v.machineId ?? run?.machineId))
  const item: InboxItem | undefined = v.item
  const toast = act.toast
  const answer = async (a: AnswerInput, ok: string) => {
    try {
      if (!(await act.answer(v, a))) return
      toast(ok)
      p.onDone?.()
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }

  const refs = useRefStore(b, {
    runId: v.runId ?? '',
    machineId: v.machineId ?? run?.machineId ?? '',
    enabled: !!v.runId && !!(v.machineId ?? run?.machineId) && !run?.readOnly,
  })
  // visual-review: HTML boards render on the artifact origin (never the app origin), like snapshots.
  const snapshotHtml = useMemo(() => snapshotRenderer(), [])
  const viewer = useMemo<ViewerContextValue>(
    () => ({
      connection: 'live',
      ...(refs ? { refs } : {}),
      ...(snapshotHtml ? { renderHtml: snapshotHtml } : {}),
    }),
    [refs, snapshotHtml],
  )
  const renderRef = (e: { kind: string; ref: string }, children: ReactNode) =>
    e.kind === 'diff' || e.kind === 'test' || e.kind === 'commit' ? (
      <RefLink
        refValue={{
          kind: e.kind === 'commit' ? 'commit' : 'path',
          text: e.ref,
          strength: 'strong',
        }}
      >
        {children}
      </RefLink>
    ) : (
      children
    )

  const agentLabel = `${v.agent === 'codex' ? 'Codex' : 'Claude'}${machine ? ` on ${machine.name}` : ''}${run?.title ? `, in “${run.title}”` : ''}`
  return (
    <ViewerContext.Provider value={viewer}>
      <DecisionView
        view={v}
        context={{
          ...(project?.name || run?.projectName
            ? { projectName: project?.name ?? run?.projectName }
            : {}),
          ...((item?.workstreamName ?? run?.workstreamName)
            ? { lane: item?.workstreamName ?? run?.workstreamName }
            : {}),
          agentLabel,
          agentShort: `${v.agent === 'codex' ? 'Codex' : 'Claude'}${machine ? ` on ${machine.name}` : ''}`,
          ...(task
            ? {
                task: {
                  title: task.title,
                  onOpen: () =>
                    p.onOpenTask
                      ? p.onOpenTask(task.id, projectId as string)
                      : void nav({
                          to: '/p/$projectId',
                          params: { projectId: projectId as string },
                          search: { i: `task:${task.id}` } as never,
                        }),
                },
              }
            : {}),
          ...(v.runId && run ? { conversationRunId: v.runId } : {}),
          // ia-shell: "Open in session" (⇧V) at this ask, in the project's chrome.
          ...(v.runId && run
            ? {
                conversationHref: `${runHref(v.runId, projectId)}?${
                  v.source === 'entity' ? `at=${encodeURIComponent(v.key)}` : `seq=${v.seq ?? ''}`
                }`,
              }
            : {}),
        }}
        {...(p.hotkeys === false ? { hotkeys: false } : {})}
        {...(run && v.seq
          ? {
              turns: ({ expanded, setExpanded }: TurnsState) => (
                <LedTurnsSection
                  run={run}
                  askSeq={v.seq as number}
                  expanded={expanded}
                  setExpanded={setExpanded}
                />
              ),
            }
          : {})}
        {...(p.showTitle ? { showTitle: true } : {})}
        {...(p.intent ? { intent: p.intent } : {})}
        {...(p.keys ? { keys: true } : {})}
        renderRef={renderRef}
        renderArtifact={(e) =>
          e.artifact && b.artifacts ? (
            <EncryptedArtifact reader={b.artifacts} reference={e.artifact} textPreview />
          ) : (
            <span className="dv-muted">Unlock this browser to preview.</span>
          )
        }
        renderMedia={(m, o) => <OptionMediaView m={m} title={o.title} />}
        {...(run && !v.seq ? { transcript: <TranscriptTail run={run} /> } : {})}
        {...(v.runId && run ? { artifacts: <ArtifactGallery runId={v.runId} /> } : {})}
        onPick={async (optionId, extra) => {
          const board = !!v.board || !!extra?.mix?.length || !!extra?.comments
          const label = board
            ? describeBoardAnswer(v.options, {
                optionId,
                ...(extra?.mix?.length ? { mix: extra.mix } : {}),
                ...(extra?.comments ? { comments: extra.comments } : {}),
              })
            : (v.options.find((o) => o.id === optionId)?.label ?? 'picked')
          const note = extra?.note?.trim()
          const files = extra?.files ?? []
          // visual-review: a mix per area and per-option comments ride in the answer
          const boardExtra = {
            ...(extra?.mix?.length ? { mix: extra.mix } : {}),
            ...(extra?.comments ? { comments: extra.comments } : {}),
          }
          // Files ride on a thread message just before the answer (the note goes with it);
          // a note alone rides in the answer itself.
          if (files.length) {
            if (v.source !== 'entity') {
              toast(
                'Files can’t ride on this answer. Remove them, or answer in the conversation.',
                'error',
              )
              return
            }
            try {
              await act.message(v, note || 'Files for my answer.', files)
            } catch (e) {
              toast(errorText(e), 'error')
              return
            }
            return answer(
              { mode: 'pick', optionId, ...boardExtra },
              `Answered: ${label}, with your files`,
            )
          }
          return answer(
            { mode: 'pick', optionId, ...(note ? { note } : {}), ...boardExtra },
            `Answered: ${label}${note ? ', with your note' : ''}`,
          )
        }}
        onFreeform={(text) => answer({ mode: 'freeform', text }, 'Answer sent')}
        onDelegate={(guidance) =>
          answer(
            { mode: 'delegate', ...(guidance ? { guidance } : {}) },
            'The agent decides and reports back',
          )
        }
        onSnooze={async (until) => {
          try {
            await act.snooze(v, until)
            toast(
              `Snoozed until ${new Date(until).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`,
            )
            p.onDone?.()
          } catch (e) {
            toast(errorText(e), 'error')
          }
        }}
        {...(v.source === 'entity' && act.store && act.routeFor(v)
          ? {
              onMessage: async (text: string, files: PendingFile[]) => {
                try {
                  await act.message(v, text, files)
                } catch (e) {
                  toast(errorText(e), 'error')
                }
              },
            }
          : {})}
      />
      {refs ? <RefInspector store={refs} /> : null}
    </ViewerContext.Provider>
  )
}

// ---- visual-review: one image / HTML board of a design option ------------------------------------

/**
 * Sealed media is opened on this device (reference checked against the signed-in account and the
 * option's artifact id); accounts without sealed artifacts use a signed URL. HTML boards render in
 * the sandboxed snapshot frame (scripts removed, no network) or an empty-sandbox frame.
 */
function OptionMediaView({ m, title }: { m: OptionMedia; title: string }) {
  const b = useBackend()
  const access = b.artifactAccess
  const trusted =
    m.artifact && m.artifact.accountId === access.accountId && m.artifact.objectId === m.ref
      ? m.artifact
      : undefined
  const signed = useQuery({
    queryKey: ['artifact-url', m.ref],
    queryFn: () => b.api.artifactUrl(m.ref),
    enabled: !m.artifact && !b.content,
    staleTime: 4 * 60_000,
    retry: false,
  })
  if (m.artifact) {
    if (!trusted || !b.artifacts)
      return <span className="dv-muted">Unlock this browser to preview.</span>
    return m.kind === 'html' ? (
      <EncryptedSnapshot reader={b.artifacts} reference={trusted} title={title} />
    ) : (
      <EncryptedArtifact reader={b.artifacts} reference={trusted} />
    )
  }
  if (signed.isError) return <span role="alert">Preview unavailable</span>
  if (!signed.data?.url) return <span role="status">Loading preview…</span>
  // Accounts without sealed artifacts (the demo build) carry the board as a data: URL; the
  // frame-src policy only allows the sandboxed srcdoc frame, never a navigated URL.
  const inline = /^data:text\/html[;,]/.test(signed.data.url)
    ? decodeURIComponent(signed.data.url.slice(signed.data.url.indexOf(',') + 1))
    : undefined
  if (m.kind === 'html')
    return inline !== undefined && snapshotRenderer() ? (
      <SandboxFrame html={inline} title={`${title} (board, scripts off)`} />
    ) : (
      <p className="dv-muted" role="status">
        {m.label ?? 'HTML board'}: the live board opens on the artifact origin, which this build
        does not have.
      </p>
    )
  return <img src={signed.data.url} alt={m.label ?? title} />
}
// ---- end visual-review ----

/** The embedded live tail of the asking run (the full viewer stays canonical). */
function TranscriptTail({ run }: { run: RunSummary }) {
  const b = useBackend()
  const view = useRunTranscript(run.runId)
  const store = useMemo(
    () => b.transcriptStore(run.runId, b.artifactAccess.accountId),
    [b, run.runId],
  )
  const sync = useProgressSync(run.runId, view, store, run.headSeq)
  return <Transcript view={view} sync={sync} composer={false} showLevelControl={false} />
}

// ---- ia-shell: "Turns that led here" in the decision's inspector (decision 5) ----------------------

interface TurnsState {
  expanded: boolean
  setExpanded: (x: boolean) => void
}

/**
 * The turns behind the ask, with the transcript components: the asking turn (cut at the ask)
 * and the turns before it, each marked "Led to this ask"; earlier turns fold behind "Show N
 * earlier turns". Collapsed, it is one line ("3 turns that led here", `v`).
 */
function LedTurnsSection({
  run,
  askSeq,
  expanded,
  setExpanded,
}: { run: RunSummary; askSeq: number } & TurnsState) {
  const b = useBackend()
  const view = useRunTranscript(run.runId)
  const store = useMemo(
    () => b.transcriptStore(run.runId, b.artifactAccess.accountId),
    [b, run.runId],
  )
  // Load the window around the ask (a long run's tail may not include it).
  useEffect(() => {
    if (expanded) void store.focusSeq(askSeq)
  }, [expanded, store, askSeq])
  const [all, setAll] = useState(false)
  const led = useMemo(
    () => ledTurns(view, askSeq, all ? Number.POSITIVE_INFINITY : LED_TURNS),
    [view, askSeq, all],
  )
  const count = led?.shown ?? LED_TURNS
  return (
    <>
      <div className="dv-sec__h">
        <h3>
          <button
            type="button"
            className="dv-link dv-led__toggle"
            aria-expanded={expanded}
            aria-keyshortcuts="v"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <ChevronDown size={14} aria-hidden="true" />
            ) : (
              <ChevronRight size={14} aria-hidden="true" />
            )}
            How it got here
            <span className="dv-led__n">
              {led ? `${count} ${count === 1 ? 'turn' : 'turns'}` : null}
            </span>
          </button>
        </h3>
        <kbd className="dv-led__key">v</kbd>
      </div>
      {expanded ? (
        led ? (
          <div className="dv-led__turns" data-led-turns={led.ids.join(' ')}>
            {led.earlier ? (
              <button type="button" className="dv-link" onClick={() => setAll(true)}>
                Show {led.earlier} earlier {led.earlier === 1 ? 'turn' : 'turns'}
              </button>
            ) : null}
            <p className="dv-led__label">Led to this ask</p>
            <Transcript
              view={led.view}
              composer={false}
              showLevelControl={false}
              defaultView="transcript"
              initialScroll="bottom"
              autoFocusAsk={false}
            />
          </div>
        ) : (
          <SkeletonRows n={3} />
        )
      ) : null}
    </>
  )
}
